import React, { useCallback, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import PropTypes from 'prop-types';
import { TextField, Grid, Autocomplete, List, Typography, CircularProgress } from '@mui/material';
import { UploadFile, UPLOAD_FILE_STATUS_KEY } from '../../../../ui';
import { useRulesSet } from './hooks/RulesSetHook';

const labels = {
  upload: {
    button: 'Importer',
    invalidFileMessage: 'Fichier invalide',
    delete: 'Supprimer',
    noFileMessage: 'Vide',
    sourceModifiee: '(source modifiée)',
    fileName: (rulesSetId) => `${rulesSetId}.csv`,
    fichierVide: 'Fichier vide',
  },
  gisements: {
    label: 'Gisements',
    noOption: 'Aucun gisement correspondant',
  },
};

const emptyFile = {
  content: null,
  file: null,
  id: null,
  name: '',
  status: UPLOAD_FILE_STATUS_KEY.EMPTY,
};

export const RulesSetTab = ({
  currentRulesSet = { selectedGisement: null, currentFile: emptyFile },
  setCurrentRulesSet,
  gisements,
  defaultSelectedGisement = gisements[0],
  editMode,
}) => {
  const {
    getRulesSetTableId,
    getRulesSetId,
    getRulesSetDatasetId,
    downloadFileFromStorage,
    isLoading,
    setIsLoading,
    fileError,
    setFileError,
  } = useRulesSet();
  const { setValue, getValues } = useFormContext();

  const selectGisement = useCallback(
    (gisement) => {
      const rulesSetTableId = getRulesSetTableId(gisement);
      const rulesSetId = getRulesSetId(gisement);
      const fileUploaded = getValues(rulesSetId);
      let currentFile;
      if (fileUploaded != null) {
        currentFile = { name: fileUploaded.name, status: UPLOAD_FILE_STATUS_KEY.READY_TO_UPLOAD };
      } else if (getRulesSetDatasetId(gisement) != null || rulesSetTableId != null) {
        currentFile = {
          name: labels.upload.fileName(rulesSetId),
          status: UPLOAD_FILE_STATUS_KEY.READY_TO_DOWNLOAD,
        };
      } else currentFile = { ...emptyFile };
      setCurrentRulesSet({ ...currentRulesSet, selectedGisement: gisement, currentFile });
    },
    [currentRulesSet, getRulesSetDatasetId, getRulesSetId, getRulesSetTableId, getValues, setCurrentRulesSet]
  );

  useEffect(() => {
    if (currentRulesSet.selectedGisement == null) selectGisement(defaultSelectedGisement);
    else selectGisement(currentRulesSet.selectedGisement);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleValueChange = useCallback(
    (newValue) => {
      const rulesSetId = getRulesSetId(currentRulesSet.selectedGisement);
      setValue(
        rulesSetId,
        {
          ...newValue,
          gisement: currentRulesSet.selectedGisement,
          tableType: 'RulesSet',
          mappingKey: 'EnsembleRegles',
        },
        { shouldDirty: true }
      );
    },
    [currentRulesSet.selectedGisement, getRulesSetId, setValue]
  );

  const handleUploadFile = useCallback(
    (event) => {
      const file = event.target.files[0];
      event.target.value = null;
      if (!file || file.type !== 'text/csv') {
        setFileError('Le fichier doit être en format .csv');
        setIsLoading(false);
        return;
      }
      const reader = new FileReader();
      reader.onload = (loadEvent) => {
        const header = loadEvent.target.result.trim().split(/\s*[\r\n]+\s*/g)[0];
        const fileToUpload = { name: file.name, status: UPLOAD_FILE_STATUS_KEY.READY_TO_UPLOAD, file };
        if (header) {
          setFileError(null);
          handleValueChange(fileToUpload);
        } else setFileError('Le fichier doit contenir une entête');
        setCurrentRulesSet({ ...currentRulesSet, currentFile: fileToUpload });
      };
      reader.readAsText(file.slice(0, 1024));
    },
    [currentRulesSet, handleValueChange, setCurrentRulesSet, setFileError, setIsLoading]
  );

  const handleDeleteFile = useCallback(() => {
    const newFile = {
      name: labels.upload.fichierVide,
      status: UPLOAD_FILE_STATUS_KEY.READY_TO_UPLOAD,
      file: new Blob([''], { type: 'text/csv' }),
    };
    handleValueChange(newFile);
    setCurrentRulesSet({ ...currentRulesSet, currentFile: newFile });
  }, [currentRulesSet, handleValueChange, setCurrentRulesSet]);

  const handleDownload = useCallback(async () => {
    try {
      await downloadFileFromStorage(currentRulesSet.selectedGisement);
    } catch (error) {
      console.error(error);
      setFileError("Le fichier associé n'existe pas");
    } finally {
      setIsLoading(false);
    }
  }, [currentRulesSet.selectedGisement, downloadFileFromStorage, setFileError, setIsLoading]);

  return (
    <Grid container direction="row" justifyContent="center" alignItems="flex-start" sx={{ minHeight: 200 }}>
      <Grid item xs={4}>
        <Autocomplete
          id="rules-set-gisement-selector"
          noOptionsText={labels.gisements.noOption}
          onChange={(event, newValue) => selectGisement(newValue)}
          value={currentRulesSet.selectedGisement}
          size="small"
          disableClearable
          disabled
          options={gisements || []}
          renderOption={(props, option) => <li {...props}>{option}</li>}
          renderInput={(params) => <TextField {...params} label={labels.gisements.label} variant="outlined" />}
        />
      </Grid>
      <Grid item>
        {isLoading ? (
          <CircularProgress data-cy="rules-set-loading-spinner" sx={{ mx: 2 }} />
        ) : (
          <Grid container sx={{ px: 4 }} direction="column" justifyContent="flex-end" alignItems="flex-start">
            <UploadFile
              acceptedFileTypes=".csv"
              key="rules-set"
              id="rules-set"
              labels={labels.upload}
              handleUploadFile={handleUploadFile}
              handleDeleteFile={handleDeleteFile}
              handleDownloadFile={handleDownload}
              file={currentRulesSet.currentFile}
              editMode={editMode && currentRulesSet.selectedGisement != null}
            />
            {getRulesSetDatasetId(currentRulesSet.selectedGisement) != null &&
              currentRulesSet.currentFile.status !== UPLOAD_FILE_STATUS_KEY.READY_TO_UPLOAD && (
                <Typography data-cy="rules-set-source-changed" sx={{ ml: 16 }} variant="subtitle2">
                  {labels.upload.sourceModifiee}
                </Typography>
              )}
            {fileError && (
              <Grid item sx={{ width: 'auto', overflow: 'auto', p: 1 }}>
                {Array.isArray(fileError) ? (
                  <List data-cy="rules-set-file-errors">
                    {fileError.map((error, index) => (
                      <li key={index}>{error}</li>
                    ))}
                  </List>
                ) : (
                  <Typography data-cy="rules-set-file-error" variant="subtitle2" color="error">
                    {fileError}
                  </Typography>
                )}
              </Grid>
            )}
          </Grid>
        )}
      </Grid>
    </Grid>
  );
};

RulesSetTab.propTypes = {
  defaultSelectedGisement: PropTypes.string,
  gisements: PropTypes.array.isRequired,
  currentRulesSet: PropTypes.object,
  setCurrentRulesSet: PropTypes.func.isRequired,
  editMode: PropTypes.bool,
};
